import { RootState, typedStore, DirtyCheck, StateKey, PromiseCacheType } from '@/store';
import axios from 'axios';
import router from '@/router';
import { markModule } from './StoreTypes';
import { Dictionary } from 'vue-router/types/router';
import VatsAPI from '@/Shared/VatsAPI';
import { ValidRole } from '@/Shared/Roles';
import { Inactivity, SetLastRefresh, CleanCookies } from '@/Shared/Inactivity';

export interface Location {
    locationId: number;
    locationParentID: number;
    companyId: number;
    name: string;
    companyName: string;
    databaseName: string;
    externallyActive: boolean;
    internallyActive: boolean;
    urlSegment: string;
}

export interface User {
    id?: string;
    userName?: string;
    defaultLocation?: string;
}

export interface ReturnAfterLogin {
    params?: Dictionary<string>;
    name?: string | undefined;
    query?: Dictionary<string | Array<(string | null)> | null | undefined>;
}

export interface UserState {
    authenticated: boolean | null;
    user: User;
    returnAfterLogin: ReturnAfterLogin;
    roles: Dictionary<Dictionary<boolean>>;
    allowedLocations: Location[];
    inactive: boolean;
    inactivityWatcher: number | null;
}

const isDirty: DirtyCheck<UserState> = {
    user: true,
    authenticated: true,
    allowedLocations: true,
    returnAfterLogin: true,
    roles: true
};
const promiseCache: PromiseCacheType<UserState> = {};

export const userModule = markModule({
    namespaced: true,
    state: {
        authenticated: null,
        user: {} as User,
        roles: {} as Dictionary<Dictionary<boolean>>,
        allowedLocations: [] as Location[],
        returnAfterLogin: {} as ReturnAfterLogin,
        inactive: false,
        inactivityWatcher: null
    } as UserState,
    getters: {
        userName: (state) => state.user.userName,
        user: (state) => state.user,
        authenticated: (state) => state.authenticated,
        allowedLocations: (state) => state.allowedLocations,
        isInRole: (state, _, root) => (role: ValidRole): boolean => {
            if (state.roles[root.currentLocation.urlSegment]) {
                return state.roles[root.currentLocation.urlSegment][role];
            }
            return false;
        },
        currentRoles: (state, _, root) => {
            return state.roles[root.currentLocation.urlSegment];
        },
        inactive: (state) => state.inactive
    },
    modules: {},
    mutations: {
        setReturnAfterLogin(state, params: ReturnAfterLogin) {
            state.returnAfterLogin = params;
        },
        setUser(state, params: User) {
            isDirty.authenticated = true;
            isDirty.roles = true;
            isDirty.allowedLocations = true;

            state.user = params;
        },
        setAllowedLocations(state, locations: Location[]) {
            isDirty.allowedLocations = false;

            state.allowedLocations = locations;
        },
        setRoles(state, payload: { locationName: string, roles: Dictionary<boolean> }) {
            isDirty.roles = false;

            const { locationName, roles } = payload;
            state.roles = Object.assign({}, state.roles, {
                [locationName]: roles
            });
        },
        setAuthenticated(state, params: boolean) {
            isDirty.authenticated = false;

            state.authenticated = params;
        },
        inactiveUser(state, params: boolean) {
            state.inactive = params;
        }
    },
    actions: {
        async login({ state, commit }, payload: { userId: string, password: string }): Promise<User> {
            const result = await axios.post<User>('/api/Account/Login', payload);
            commit('setUser', result.data);
            // these are missing awaits because they're primarily data hydration,
            // rather than something necessary for later on in the method
            // So we can save some time by not waiting for them to complete and
            // just kick them off and ignore the result
            typedStore.modules.userModule.actions.getAuthenticationStatus();
            typedStore.modules.userModule.actions.getAllowedLocations();
            promiseCache.roles = {};
            typedStore.modules.userModule.actions.getCurrentRoles();
            SetLastRefresh(new Date());
            if (state.returnAfterLogin.name) {
                router.push(state.returnAfterLogin);
                // Clear data so that next login doesn't trigger it
                commit('setReturnAfterLogin', {});
            }

            return result.data;
        },
        async logout({ state, commit }) {
            try {
                await typedStore.modules.userModule.actions.removeChecker();
                commit('setUser', {});
                await axios.post('/api/Account/Logout');
            } catch (e) {
                if (e.response.status === 401) {
                    await typedStore.modules.userModule.actions.removeChecker();
                    commit('setUser', {});
                }
            }
        },
        async removeChecker({ state, commit }) {
            if (state.inactivityWatcher != null) {
                clearInterval(state.inactivityWatcher);
                state.inactivityWatcher = null;
                CleanCookies();
            }
        },
        async refreshSession({ state, commit }) {
            SetLastRefresh(new Date());
            try {
                await axios.post('/api/Account/RefreshSession');
            } catch (e) {
                if (e.response.status === 401) {
                    await typedStore.modules.userModule.actions.removeChecker();
                    commit('setUser', {});
                }
            }
        },
        async startInactiveUserCheck({ state, commit }) {
            state.inactivityWatcher = setInterval(
                async () => {
                    switch (Inactivity(state.inactive)) {
                        case 1:
                            commit('inactiveUser', true);
                            return;
                        case 2:
                            commit('inactiveUser', false);
                            await typedStore.modules.userModule.actions.refreshSession();
                            return;
                        default:
                            return;
                    }
                }, 1000);
        },
        async getAuthenticationStatus({ state, commit }): Promise<boolean | null> {
            if (!promiseCache.authenticated &&
                (state.authenticated === null || isDirty.authenticated)) {
                promiseCache.authenticated =
                    axios.get<boolean>('/api/Account/AuthenticationStatus').then(resp => resp.data);
            }

            if (promiseCache.authenticated) {
                const result = await promiseCache.authenticated;
                commit('setAuthenticated', result);
                promiseCache.authenticated = null;
            }

            return state.authenticated;
        },
        async getUser({ state, commit }): Promise<User> {
            const authenticated = await typedStore.modules.userModule.actions.getAuthenticationStatus();
            if (authenticated && !promiseCache.user && isDirty.user) {
                promiseCache.user = axios.get<User>('/api/Account/User').then(resp => resp.data);
            }

            if (promiseCache.user) {
                const user = await promiseCache.user;
                commit('setUser', user);

                promiseCache.user = null;
            }

            return state.user;
        },
        async getCurrentRoles({ state, commit, rootState }): Promise<Dictionary<boolean>> {
            const location = rootState.currentLocation;
            if (!promiseCache.roles) {
                promiseCache.roles = {};
            }

            const authenticated = await typedStore.modules.userModule.actions.getAuthenticationStatus();
            if (!location || !location.urlSegment || !authenticated) {
                return {};
            }
            if (!state.roles[location.urlSegment] || isDirty.roles) {
                if (!promiseCache.roles![location.urlSegment]) {
                    const promise = VatsAPI.get<string[]>(`Account/Roles/${location.urlSegment}`).
                        then(response => response.data.reduce((dictionary: Dictionary<boolean>, role) => {
                            dictionary[role] = true;
                            return dictionary;
                        }, {}));
                    promiseCache.roles![location.urlSegment] = promise;
                }

                const result = await promiseCache.roles![location.urlSegment];
                commit('setRoles', {
                    locationName: location.urlSegment,
                    roles: result
                });

                delete promiseCache.roles![location.urlSegment];
            }
            return state.roles[location.urlSegment];
        },
        async getAllowedLocations({ state, commit }): Promise<Location[]> {
            if (!state.allowedLocations ||
                !state.allowedLocations.length ||
                isDirty.allowedLocations) {
                if (!promiseCache.allowedLocations) {
                    const result = axios.get<Location[]>('/api/Account/Locations').then(resp => resp.data);
                    promiseCache.allowedLocations = result;
                    commit('setAllowedLocations', await result);
                    result.finally(() => {
                        promiseCache.allowedLocations = null;
                    });
                }
                return promiseCache.allowedLocations!;
            }
            return state.allowedLocations;
        },
    },
}, {} as RootState);
